// "See it in 3D" on the check dialog: the customer's design on a 3D luggage cover, and their own suitcase size (Ryan, 3 Nov 2026).
// Luggage covers only (the shapes in SHAPES_3D).
//
// The size box works like the one on the store's product page (trolley-size.js has the rules): height, width, depth in cm or in;
// the 3D case follows the numbers as they are typed; when the customer leaves the boxes (or presses Enter) the size is checked.
//   - A good size picks the price band (Kids, Small, Medium, Large): the order goes to that size of the product (ctx.variant), and
//     the size is saved with the design (customSize), so the cart line gets "Custom size (cm)" and the staff page shows it.
//   - A size that is too big, too small or half typed stops Confirm until it is fixed or cleared (problem()), as on the product page.
//   - "Clear my size" goes back to the size chosen on the product page.
// A size typed on the product page comes in with the link (csize, cunit) and counts as the customer's size from the start.
// For a bulk order the sizes come from the list: the size box is hidden and no size is saved.
// The 3D part (three.js, suitcase3d.js) is loaded only when the sheet opens. Without WebGL the sheet says so; the size box still works.

import { $, esc } from "./util.js";
import * as TS from "./trolley-size.js";
import { previewPNG } from "./svg-export.js";
import { formatPrice } from "./bulk.js";
import { isBackSide } from "./products.js";

const SHAPES_3D = new Set(["trolley-bag-cover"]);
const KEYS = ["h", "w", "d"];
const DRAW_MAX = { h: 120, w: 100, d: 80 };          // the 3D case never draws bigger than this while typing
const TYPE_WAIT_MS = 350;                             // the case waits until the customer stops typing ("60" does not draw "6" first)

// editor: the main editor. ctx: { shop, handle, variant, returnUrl, embed } (main.js; ctx.variant changes with the size band).
// link: { csize, cunit } from the page address. onChange(): the size or its problem changed (the check dialog shows it again).
export function initView3d({ editor, ctx, link = {}, onChange = () => {} }) {
  const sheet = $("view3d"), canvas = $("v3dCanvas"), wait = $("v3dWait"), none = $("v3dNone"), msg = $("v3dMsg");
  const row = $("see3dRow"), rowSize = $("see3dSize"), sizeBox = $("v3dSize"), lead = $("szLead");
  const inputs = { h: $("szH"), w: $("szW"), d: $("szD") };
  const previewMode = !ctx.returnUrl && !ctx.embed;
  const linkVariant = String(ctx.variant || "");        // the size chosen on the product page (the link's variant)

  const S = { unit: "cm", typed: { h: "", w: "", d: "" }, applied: null, problem: null, key: "" };
  let baseLabel = "";                // the size chosen on the product page ("Small (20-inch trolley)"): Clear my size goes back to it
  let viewer = null, failed = false, mod = null, openToken = 0, lastFocus = null, typeT = 0;

  const available = () => SHAPES_3D.has(String((editor.product && editor.product.shape) || ""));
  const single = () => editor.bulk.mode() === "single";
  const sizes = () => { const i = editor.bulk.info(); return (i && Array.isArray(i.sizes)) ? i.sizes : []; };
  const labelOf = (variant) => { const s = sizes().find((x) => x.variantId && x.variantId === String(variant || "")); return s ? s.label : ""; };
  const fmt = (n) => String(TS.round1(n));

  // A size from the product page.
  const fromLink = TS.readLinkSize(link.csize, link.cunit);
  if (fromLink) {
    S.unit = fromLink.unit;
    S.applied = { size: fromLink.size, band: fromLink.band, typedIn: fromLink.typedIn };
    S.typed = fromTyped(fromLink.unit === "in" ? fromLink.typedIn : [fromLink.size.h, fromLink.size.w, fromLink.size.d]);
    S.key = keyOf();
  }
  function fromTyped(list) { return { h: fmt(list[0]), w: fmt(list[1]), d: fmt(list[2]) }; }
  function keyOf() { return [S.unit, S.typed.h, S.typed.w, S.typed.d].join("|"); }

  // The band of the size chosen on the product page, and the size the 3D case shows when the customer has not typed one.
  function baseBand() { return TS.bandOfLabel(baseLabel || labelOf(linkVariant)) || TS.SIZE_BANDS[2]; }
  function shownDims() {
    if (S.applied) return S.applied.size;
    const std = baseBand().std, out = {};
    KEYS.forEach((k) => {
      const n = TS.parseNum(S.typed[k]), cm = n > 0 ? TS.toCm(n, S.unit) : 0;
      out[k] = cm ? Math.max(TS.MIN_CM[k], Math.min(cm, DRAW_MAX[k])) : std[k];
    });
    return out;
  }

  // ---------- the order follows the size ----------
  // Puts the order on the shop's size for a band. Returns that size of the shop, or null when the shop's sizes are not known.
  function useBand(band) {
    const s = TS.sizeForBand(band, sizes());
    if (s && s.variantId) ctx.variant = s.variantId;
    return s;
  }
  function backToBase() {
    const s = baseLabel ? sizes().find((x) => x.label === baseLabel) : null;
    if (s && s.variantId) ctx.variant = s.variantId;
  }
  // The shop's sizes can arrive after the page opened, and change with another background: keep the order on the right size.
  function sync() {
    if (!baseLabel) baseLabel = labelOf(linkVariant);
    if (S.applied && single()) useBand(S.applied.band);
    render();
  }

  // ---------- checking the typed size ----------
  function apply(force = false) {
    const key = keyOf();
    if (!force && key === S.key) return;
    S.key = key;
    const empty = KEYS.every((k) => !String(S.typed[k]).trim());
    if (empty) { clear(); return; }
    const r = TS.checkSize(S.typed, S.unit);
    if (!r.ok) {
      S.applied = null; S.problem = { kind: r.problem, bad: r.bad };
    } else {
      const s = useBand(r.band);
      if (!s && !previewMode) { S.applied = null; S.problem = { kind: "price", bad: [] }; }
      else { S.applied = { size: r.size, band: r.band, typedIn: r.typedIn }; S.problem = null; }
    }
    if (viewer) viewer.setDims(shownDims());
    render(); onChange();
  }
  function clear() {
    S.typed = { h: "", w: "", d: "" }; S.applied = null; S.problem = null; S.key = keyOf();
    backToBase();
    if (viewer) viewer.setDims(shownDims());
    render(); onChange();
  }

  // ---------- what the customer sees ----------
  const CLEAR = '<button type="button" class="linkbtn" data-clear>Clear my size</button>';
  function waButton(problem) {
    const href = TS.whatsAppLink({ title: editor.product.title, typed: S.typed, unit: S.unit, problem, page: ctx.returnUrl || location.href });
    return `<a class="btn wa" href="${esc(href)}" target="_blank" rel="noopener">Ask us on WhatsApp</a>`;
  }
  function render() {
    row.hidden = !available();
    rowSize.textContent = S.applied && single() ? `Your suitcase: ${TS.showCm(S.applied.size)}` : "";
    sizeBox.hidden = !single();
    $("szUnit").querySelectorAll("[data-unit]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.unit === S.unit)));
    sizeBox.querySelectorAll(".suffix").forEach((x) => { x.textContent = S.unit; });
    const std = baseBand().std;
    KEYS.forEach((k) => {
      const el = inputs[k];
      if (document.activeElement !== el && el.value !== S.typed[k]) el.value = S.typed[k];
      el.placeholder = fmt(TS.fromCm(std[k], S.unit));
      el.setAttribute("aria-label", `${TS.NAMES[k]} in ${S.unit}`);
      el.setAttribute("aria-invalid", String(!!(S.problem && S.problem.bad.includes(k))));
    });
    const base = baseLabel || labelOf(linkVariant);
    lead.textContent = `Optional. Measure the case body only. Leave out the wheels and handle.${!S.applied && base ? ` Now showing ${base.split(" (")[0]}, about ${TS.showCm(std)}.` : ""}`;
    if (S.problem) {
      const words = S.problem.kind === "price" ? "We could not check the price for this size. Check your internet, then try again." : TS.problemText(S.problem.kind);
      const off = S.problem.kind === "over" ? "" : " Confirm is off until you fix your size.";
      msg.className = "v3d-msg err";
      msg.innerHTML = `<p>${esc(words + off)}</p><div class="acts">${waButton(words)}${CLEAR}</div>`;
    } else if (S.applied) {
      const s = TS.sizeForBand(S.applied.band, sizes());
      const price = s && s.price ? `<strong>Your price: ${esc(formatPrice(s.price))}</strong> (same as ${esc(S.applied.band.size)})` : `Same price as ${esc(S.applied.band.size)}.`;
      msg.className = "v3d-msg ok";
      msg.innerHTML = `<p>${price}</p><p>We will make your cover ${esc(TS.showCm(S.applied.size))}.</p><div class="acts">${CLEAR}</div>`;
    } else { msg.className = "v3d-msg"; msg.innerHTML = ""; }
  }

  // ---------- events ----------
  sizeBox.addEventListener("input", (e) => {
    const el = e.target.closest("[data-dim]"); if (!el) return;
    const clean = el.value.replace(/[^0-9.,]/g, "");
    if (clean !== el.value) el.value = clean;
    S.typed[el.dataset.dim] = clean;
    clearTimeout(typeT);
    typeT = setTimeout(() => { if (viewer) viewer.setDims(shownDims()); }, TYPE_WAIT_MS);
  });
  // Leaving the three boxes (not moving between them) checks the size.
  sizeBox.addEventListener("focusout", (e) => {
    if (!e.target.closest("[data-dim]")) return;
    const next = e.relatedTarget;
    if (next && next.closest && next.closest("[data-dim]")) return;
    apply(false);
  });
  sizeBox.addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || !e.target.closest("[data-dim]")) return;
    e.preventDefault(); apply(true);
  });
  sizeBox.addEventListener("click", (e) => {
    const u = e.target.closest("[data-unit]");
    if (u && u.dataset.unit !== S.unit) {
      const to = u.dataset.unit;
      KEYS.forEach((k) => { const n = TS.parseNum(S.typed[k]); if (n > 0) S.typed[k] = fmt(to === "in" ? n / TS.CM_PER_IN : n * TS.CM_PER_IN); });
      S.unit = to;
      apply(true);
      return;
    }
    if (e.target.closest("[data-clear]")) clear();
  });
  $("btnSee3d").addEventListener("click", open);
  $("btnClose3d").addEventListener("click", close);
  $("btnDone3d").addEventListener("click", close);
  $("v3dViews").addEventListener("click", (e) => {
    const b = e.target.closest("[data-view]"); if (!b || !viewer) return;
    viewer.view(b.dataset.view);
    $("v3dViews").querySelectorAll("[data-view]").forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
  });
  // Esc closes the sheet (not the check dialog under it); Tab stays inside the sheet.
  document.addEventListener("keydown", (e) => {
    if (sheet.hidden) return;
    if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); close(); return; }
    if (e.key !== "Tab") return;
    const items = [...sheet.querySelectorAll("button, input, a[href]")].filter((el) => !el.disabled && el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (!sheet.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    e.stopImmediatePropagation();
  }, true);

  // ---------- the sheet ----------
  // The pictures of the front and the back: the same as "what we print" (print area only), so the 3D shows what is printed.
  async function pictures() {
    const list = editor.sides(), cover = editor.currentCover();
    const front = list.find((s) => !isBackSide(s.id)) || list[0], back = list.find((s) => isBackSide(s.id)) || null;
    const load = async (s) => {
      if (!s) return null;
      const blob = await previewPNG(editor, cover, s.index);
      if (typeof createImageBitmap === "function") return createImageBitmap(blob);
      const url = URL.createObjectURL(blob);
      try { return await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; }); } finally { URL.revokeObjectURL(url); }
    };
    return { front: await load(front), back: await load(back) };
  }
  async function open() {
    if (!available()) return;
    lastFocus = document.activeElement;
    const token = ++openToken;
    sheet.hidden = false; render();
    $("btnClose3d").focus();
    wait.hidden = false; none.hidden = true; canvas.hidden = false; $("v3dViews").hidden = $("v3dHint").hidden = false;
    try {
      if (!mod) mod = await import("./suitcase3d.js");
      if (!viewer && !failed) { viewer = mod.createSuitcase3D({ canvas, dims: shownDims() }); failed = !viewer; }
    } catch (e) { console.error("RightGifting: 3D could not start", e); failed = true; }
    if (token !== openToken || sheet.hidden) return;
    if (failed) { wait.hidden = true; none.hidden = false; canvas.hidden = true; $("v3dViews").hidden = $("v3dHint").hidden = true; return; }
    try {
      const pics = await pictures();
      if (token !== openToken || sheet.hidden) return;
      viewer.setPictures(pics.front, pics.back);
      [pics.front, pics.back].forEach((p) => { if (p && typeof p.close === "function") p.close(); });     // copied into the 3D pictures: free the memory
    } catch (e) { console.error("RightGifting: the 3D pictures could not be made", e); }
    viewer.setDims(shownDims(), { animate: false });
    viewer.view("front");
    $("v3dViews").querySelectorAll("[data-view]").forEach((x) => x.setAttribute("aria-pressed", String(x.dataset.view === "front")));
    viewer.start();
    wait.hidden = true;
  }
  function close() {
    if (sheet.hidden) return;
    openToken++;
    const a = document.activeElement;
    if (a && a.closest && a.closest("[data-dim]")) apply(false);       // a size typed and not yet checked is checked now
    if (viewer) viewer.stop();
    sheet.hidden = true;
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus();
    onChange();
  }

  render();
  return {
    available,
    open, close, sync,
    isOpen: () => !sheet.hidden,
    // The size saved with the design ({ h, w, d } in cm, and typedIn), or null.
    customSize: () => (S.applied && single() && available() ? TS.designSize(S.applied.size, S.applied.typedIn) : null),
    // What stops Confirm ("" when nothing does).
    problem: () => (S.problem && single() && available() ? "Your suitcase size needs fixing. Tap “See it in 3D” to fix it, or clear your size there." : ""),
    sizeText: () => (S.applied && single() && available() ? TS.showCm(S.applied.size) : "")
  };
}
